import api from '../services/api';

/* ==========================================================================
   Statistiky → Provize (sestava) :: MOST MEZI API A OBRAZOVKOU

   Nahrazuje statisticsMockData.js. Obrazovka pracuje s tvary z mockupu
   (období „2026/04", položka s jAuto/kAuto/*Override), API vrací databázový
   tvar a spočítané řádky z CommissionEngine. Překlad je schválně tady.

   Výpočet dělá backend — obrazovka si J/K/L/M přepočítává jen lokálně při
   editaci buňky, aby byla odezva okamžitá. Zdroj pravdy je engine.
   ========================================================================== */

/** 1 inzerát, 2–4 inzeráty, 5+ inzerátů. */
const sklonujInzerat = (n) => (n === 1 ? 'inzerát' : (n >= 2 && n <= 4 ? 'inzeráty' : 'inzerátů'));

/**
 * Uložený ruční přepis řádku (`commission_override`, migrace 049) přeložený
 * do názvů, se kterými pracuje obrazovka.
 *
 * Vstupy (dpd, ceníková cena, sleva, úhrada) už jsou v hodnotách z enginu
 * započítané — tady slouží jen k tomu, aby obrazovka poznala, která buňka je
 * ruční, kdo ji zadal a co vrátit zpět.
 */
const prelozPrepis = (ov) => (ov ? {
  dpd: ov.dpd ?? null,
  cenikovaCena: ov.list_price ?? null,
  slevaPct: ov.discount_pct ?? null,
  platbaBezDph: ov.paid_ex_vat ?? null,
  j: ov.j ?? null,
  k: ov.k ?? null,
  l: ov.l ?? null,
  m: ov.m ?? null,
  duvod: ov.reason ?? null,
  autor: ov.author ?? null,
  kdy: ov.updatedAt ?? null,
} : null);

/** Má řádek vůbec nějaký uložený přepis? */
export const maPrepis = (prepis) => !!prepis && ['dpd', 'cenikovaCena', 'slevaPct', 'platbaBezDph', 'j', 'k', 'l', 'm']
  .some((p) => prepis[p] != null);

/** Období: API pracuje s `2026-04`, obrazovka zobrazuje `2026/04`. */
export const naObrazovku = (obdobi) => String(obdobi || '').replace('-', '/');
export const naApi = (obdobi) => String(obdobi || '').replace('/', '-');

/** Seznam období, za která existují uhrazené faktury. */
export async function nactiObdobi() {
  const { data } = await api.getStatisticsPeriods();
  return data.map((o) => ({
    id: naObrazovku(o.period),
    apiId: o.period,
    pocetFaktur: o.invoiceCount,
    uzavreno: !!o.state?.closed,
  }));
}

/**
 * Sestava provizí za období.
 *
 * Vrací i číselník korekcí splatnosti — bez něj by si obrazovka musela
 * hranice 45/90 dní držet zadrátované a nesledovala by změnu v Nastavení.
 */
export async function nactiSestavu(obdobi) {
  const { data } = await api.getStatisticsCommissions(naApi(obdobi));

  const prepisy = data.overrides || {};
  const obd = naObrazovku(data.period);

  return {
    obdobi: obd,
    totals: data.totals,
    /* Stav uzávěrky ze serveru — uzavřené období se čte z otisku a needituje. */
    uzavreno: !!data.periodState?.closed,
    stavObdobi: data.periodState || null,
    otisk: data.snapshot || null,
    muzeUpravovat: data.canEdit !== false,
    muzeUzavirat: data.canClose !== false,
    obchodnici: data.bySalesperson.map((o) => ({
      id: o.salespersonId,
      jmeno: o.name,
      pocetFaktur: o.invoiceCount,
      obratCelkem: o.turnoverTotal,
      provizeCelkem: o.commissionTotal,
      bonus: o.bonus,
    })),
    /* Jeden řádek = jeden uhrazený daňový doklad. Sazby na řádku jsou
       průměry vážené úhradou, položky faktury jsou v `radky` pro rozpad. */
    polozky: data.invoices.map((f) => prelozFakturu(f, obd, prepisy)),
    /* Odvozené řádky — cizí faktury přepočítané pevnou sazbou:
       superprovize manažera z týmu a mentoring z mentorovaného. V sestavě se
       kreslí jako běžné řádky (jen se needitují), stejně jako v sestavě,
       kterou klient používal v Contractu. */
    odvozene: [
      ...(data.superCommissions || []).map((b) => ({
        druh: 'super',
        ozId: b.salespersonId,
        nazev: b.name,
        role: b.role,
        pct: b.pct,
        rozsah: b.scope,
        typMedia: b.mediaType,
        pocetPodrizenych: b.subordinateCount,
        zaklad: b.base,
        provize: b.commission,
        invoices: b.invoices,
      })),
      ...(data.mentorCommissions || []).map((b) => ({
        druh: 'mentor',
        ozId: b.salespersonId,
        nazev: b.name,
        mentorovany: b.menteeName,
        pct: b.pct,
        platiOd: b.validFrom,
        platiDo: b.validTo,
        zaklad: b.base,
        provize: b.commission,
        invoices: b.invoices,
      })),
    ].map((b) => ({
      ...b,
      invoices: undefined,
      polozky: (b.invoices || []).map((f) => ({
        ...prelozFakturu(f, obd, {}),
        id: f.superKey,
        fakturaId: f.invoiceId,
        super: true,
        druh: b.druh,
        superPct: f.superPct,
        zdroj: b.druh === 'mentor' ? b.mentorovany : null,
        ozId: b.ozId,
      })),
    })),
  };
}


/** Jedna faktura ze `CommissionEngine::byInvoice()` do tvaru obrazovky. */
function prelozFakturu(f, obdobi, prepisy) {
  const ov = prelozPrepis(prepisy[f.invoiceId]);
  const vydani = f.issueNames || [];
  const inzeraty = f.bookingItemNumbers || [];
  const typy = f.mediaTypes || [];

  return {
    id: f.invoiceId,
    obdobi,

    cisloFaktury: f.invoiceNumber,
    zakaznik: f.customer,
    inzerent: f.advertiser || f.customer,
    cisloInzeratu: inzeraty.length > 1 ? `${inzeraty[0]} +${inzeraty.length - 1}` : (inzeraty[0] || '—'),
    inzeraty,
    vydaniNazev: vydani.length > 1 ? `${vydani.length} vydání` : (vydani[0] || '—'),
    vydaniSeznam: vydani.join(', '),
    mutaceNazev: (f.mutationNames || []).join(', '),
    typInzerce: `${inzeraty.length} ${sklonujInzerat(inzeraty.length)}`,
    typMedia: typy.length === 1 ? typy[0] : 'mix',
    sazbaDph: f.vatRate != null ? f.vatRate / 100 : null,

    obchodnikId: f.salespersonId,
    obchodnik: f.salespersonName,

    cenikovaCena: f.listPrice,
    platbaBezDph: f.base,
    slevaPct: f.discountPct,
    dpd: f.dpd,

    /* Uložené ruční přepisy (migrace 049). Lokální dopočet při editaci
       jede přes *Override, aby odezva byla okamžitá; po uložení se tytéž
       hodnoty vrátí ze serveru už započítané v j/k/l/m. */
    jAuto: f.jAuto,
    kAuto: f.kAuto,
    jOverride: ov?.j ?? null,
    kOverride: ov?.k ?? null,
    lOverride: ov?.l ?? null,
    mOverride: ov?.m ?? null,
    prepis: ov,

    radky: (f.lines || []).map((l) => ({
      id: l.id,
      cislo: l.itemNumber,
      inzerat: l.bookingItemNumber,
      nazev: l.name,
      vydani: l.issueName,
      mutace: l.mutationName,
      titulId: l.titleId,
      titul: l.titleName,
      typMedia: l.mediaType,
      slevovy: l.discountLine,
      platbaBezDph: l.paidExVat,
      j: l.j,
      jDuvod: l.jReason,
      k: l.k,
      l: l.l,
      m: l.m,
    })),

    rucni: !!f.manual,
    rucniId: f.manualItemId ?? null,
    poznamka: f.note || '',
  };
}


/**
 * Vyrobí funkci `pasmoSplatnosti(dpd)` z číselníku korekcí.
 *
 * Nahrazuje stejnojmennou konstantu z mockupu, aby obrazovka respektovala
 * hranice nastavené v Nastavení sazeb, a ne zadrátovaných 45/90 dní.
 */
export function vyrobPasmoSplatnosti(korekce) {
  const pasma = (korekce || [])
    .slice()
    .sort((a, b) => a.daysTo - b.daysTo)
    .map((k) => ({
      kod: k.coefficient >= 1 ? 'ok' : (k.coefficient > 0 ? 'half' : 'zero'),
      do: k.daysTo,
      koef: k.coefficient,
      nazev: k.name,
      popis: k.note,
      ikona: k.coefficient >= 1 ? 'bi-check-circle-fill'
        : (k.coefficient > 0 ? 'bi-exclamation-triangle-fill' : 'bi-x-circle-fill'),
    }));

  return (dpd) => {
    const d = Number(dpd) || 0;
    return pasma.find((p) => d <= p.do)
      || pasma[pasma.length - 1]
      || { kod: 'zero', do: null, koef: 0, nazev: '—', popis: '', ikona: 'bi-x-circle-fill' };
  };
}

/** Číselník korekcí splatnosti a seznam titulů — pro ruční položku a rozpad. */
export async function nactiCiselniky() {
  const { data } = await api.getStatisticsSettings();
  return {
    korekce: data.dueCorrections,
    pasmoSplatnosti: vyrobPasmoSplatnosti(data.dueCorrections),
    tituly: data.titles.map((t) => ({
      id: t.id, kod: t.code, nazev: t.name, typ: t.type,
    })),
    obchodnici: data.salespeople
      .filter((o) => o.isActive)
      .map((o) => ({ id: o.id, jmeno: o.name })),
  };
}
